import UsersAgent from "../agents/UsersAgent";
import {createEffect, createResource, createSignal} from "solid-js";
/*#if _IS_PROD
import LogRocket from 'logrocket';
//#endif */

// Un lien expiré et un lien trafiqué doivent produire le MÊME message : dire
// lequel des deux a échoué confirmerait à un attaquant que sa signature passe.
//
// On ne dit PAS "connectez-vous" tout court : cette feature existe justement
// pour des clients qui n'ont jamais défini de mot de passe. On les oriente vers
// "mot de passe oublié", qui fonctionne pour eux (requestPasswordReset ne teste
// ni "verified" ni l'existence d'un mot de passe préalable).
const MAGIC_LINK_ERROR = "Ce lien n'est plus valide. Connectez-vous, ou utilisez « Mot de passe oublié » si vous n'en avez jamais défini.";

export function Users (store) {

    // Agent
    const agent = new UsersAgent(store);

    // Init
    store.on('@init', (state) => ({
        agents: {
            ...state.agents,
            users: agent,
        },
        loading: {
            ...state.loading,
            currentUser: true
        },
        currentUser: null,
        loginResult: null,
        magicLinkError: null
    }));

    // Actions
    const [getCurrent, setGetCurrent] = createSignal(false);
    const [current, { refetch: refetchCurrent }] = createResource(getCurrent, agent.current.bind(agent));
    createEffect(() => store.dispatch("setCurrentUser", current()));
    createEffect(() => current.loading && store.dispatch("setCurrentLoading", current.loading));
    store.on('setCurrentLoading', (state, currentUser) => ({loading:{...state.loading, currentUser}}))

    store.on('pullUser', (state, params) => {
        setGetCurrent(true);
        if (params && params.refetch) {
            refetchCurrent();
        }
    });

    store.on("setCurrentUser", (state, currentUser) => {
        /*#if _IS_PROD
        if (currentUser && currentUser.id) {
            LogRocket.identify(currentUser.id, {
                name: `${currentUser.firstName} ${currentUser.lastName}`,
                email: currentUser.emailAddress,
            });
        }
        //#endif */
        store.dispatch("setCurrentLoading", false);
        const isOrdersPage = typeof window !== "undefined" && /^\/user\/orders(?:\/|$)/.test(window.location.pathname);
        if (currentUser && currentUser.id && isOrdersPage) {
            // Les données de liste d'attente n'appartiennent qu'à cette page et
            // ne partent qu'après résolution de l'utilisateur : pas de requête
            // Owner anonyme, ni de chargement global à chaque pullUser.
            store.dispatch("loadMyWaitingListEntries", { userId: currentUser.id });
        } else if (!currentUser) {
            store.dispatch("clearMyWaitingListEntries");
        }
        return { currentUser };
    })

    // Lien magique : la session doit exister AVANT que les réservations ne se
    // chargent, sinon myReservations répond sur une session anonyme et la page
    // annonce "aucune réservation" à un client qui en a. dispatch() de storeon
    // ne renvoie pas de promesse : la vue ne peut pas séquencer elle-même, le
    // chaînage vit donc ici (même raison que "logout" -> "loadCart" plus bas).
    store.on("authenticateMagicLink", async (state, token) => {
        store.dispatch("setCurrentLoading", true);
        let failed;
        try {
            const result = await agent.authenticateWithMagicLink(token);
            // Agent.send RENVOIE une Error sur échec GraphQL/réseau au lieu de lever.
            failed = !result || result instanceof Error || !!result.errorCode;
        } catch (err) {
            // Si send lève malgré tout, sans ce catch la promesse partirait en
            // rejet non géré : setCurrentLoading resterait à true et la page
            // afficherait "Chargement…" indéfiniment, sans message.
            failed = true;
        }
        store.dispatch("setMagicLinkError", failed ? MAGIC_LINK_ERROR : null);

        // Le jeton est une VRAIE crédence, et il vit dans le chemin de l'URL :
        // il fuit par le Referer, l'historique, les marque-pages, les logs de
        // proxy — et par LogRocket, qui enregistre les URL en production (cf.
        // l'import plus haut). On le retire de la barre d'adresse dès qu'il a
        // servi ; il reste valide côté serveur, mais cesse d'être recopié partout.
        if (typeof window !== "undefined" && window.history && window.history.replaceState) {
            window.history.replaceState(null, "", "/user/orders");
        }

        if (failed) {
            // Inutile d'interroger le serveur en anonyme : on empilerait une
            // seconde erreur par-dessus le message du lien. On rend la main au
            // rendu, qui affichera "Connectez-vous".
            store.dispatch("setCurrentUser", null);
            store.dispatch("setCurrentLoading", false);
            return;
        }

        // refetch obligatoire : la ressource a pu être résolue en anonyme avant
        // que le cookie de session n'existe.
        store.dispatch("pullUser", { refetch: true });
        store.dispatch("loadMyReservations", { skip: 0, refetch: true });
    });

    store.on("setMagicLinkError", (state, magicLinkError) => ({ magicLinkError }));
    store.on("clearMagicLinkError", () => ({ magicLinkError: null }));

    store.on("logout", async (state) => {
        store.dispatch("setCurrentUser", null);
        store.dispatch("clearConfirmedDuplicates");
        await agent.logout();
        store.dispatch("loadCart", { refetch: true });
    });

}
